import { useEffect, useState } from "react";
import { Archive, ChevronRight, CreditCard, Download, Plus, RotateCcw, Search, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, download } from "@/lib/api";

const EMPTY = { nis: "", nisn: "", name: "", gender: "Laki-laki", class_name: "", major: "Umum", entry_year: "2026", academic_year: "2026/2027", phone: "", parent_name: "", parent_phone: "" };
const FIELDS = [["nis", "NIS", true], ["nisn", "NISN"], ["name", "Nama lengkap", true], ["class_name", "Kelas", true], ["major", "Program keahlian"], ["entry_year", "Tahun masuk"], ["academic_year", "Tahun ajaran"], ["phone", "No. WhatsApp siswa"], ["parent_name", "Nama orang tua/wali"], ["parent_phone", "No. WhatsApp orang tua/wali"]];

const Modal = ({ title, close, children, testid }) => (
  <div className="modal-backdrop"><div className="modal" data-testid={testid}>
    <div className="modal-head"><h3>{title}</h3><button onClick={close} data-testid={`${testid}-close`}><X size={18} /></button></div>{children}
  </div></div>
);

const AddStudent = ({ close, saved }) => {
  const [f, setF] = useState(EMPTY);
  const submit = async (e) => {
    e.preventDefault();
    try { await api("/students", { method: "POST", body: JSON.stringify(f) }); toast.success("Data siswa ditambahkan"); saved(); close(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title="Tambah siswa" close={close} testid="add-student-modal">
      <form onSubmit={submit} className="modal-form">
        {FIELDS.map(([k, l, req]) => <label key={k}>{l}{req ? " *" : ""}<input data-testid={`student-${k}-input`} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>)}
        <label>Jenis kelamin<select data-testid="student-gender-select" value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}><option>Laki-laki</option><option>Perempuan</option></select></label>
        <button className="primary-btn full" data-testid="save-student-button">Simpan siswa</button>
      </form>
    </Modal>
  );
};

const ImportStudents = ({ close, saved }) => {
  const [file, setFile] = useState(null);
  const [update, setUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = new FormData(); body.append("file", file);
    try {
      const res = await fetch(`${API}/students/import?mode=${update ? "update" : "skip"}`, { method: "POST", body, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Gagal mengunggah file");
      setResult(data); saved(); toast.success(`${data.created} siswa ditambahkan, ${data.updated} diperbarui`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="Unggah data siswa massal" close={close} testid="import-modal">
      <div className="modal-form">
        <div className="import-step"><b>1. Unduh template</b><span>Isi data siswa pada template Excel (kolom NIS, Nama, Kelas wajib).</span>
          <button type="button" className="outline-btn" onClick={() => download("/students/template")} data-testid="download-template-button"><Download size={16} /> Unduh template .xlsx</button></div>
        <form onSubmit={submit} className="import-step"><b>2. Unggah file</b>
          <input type="file" accept=".xlsx" required onChange={(e) => { setFile(e.target.files[0]); setResult(null); }} data-testid="import-file-input" />
          <label className="check-row"><input type="checkbox" checked={update} onChange={(e) => setUpdate(e.target.checked)} data-testid="import-update-checkbox" /> Perbarui data jika NIS sudah terdaftar (jika tidak dicentang, baris dilewati)</label>
          <button className="primary-btn full" disabled={busy || !file} data-testid="import-submit-button"><Upload size={16} /> {busy ? "Mengunggah…" : "Unggah & proses"}</button>
        </form>
        {result && (
          <div className="import-result" data-testid="import-result">
            <p><b data-testid="import-created-count">{result.created}</b> baru · <b data-testid="import-updated-count">{result.updated}</b> diperbarui · <b data-testid="import-failed-count">{result.failed.length}</b> gagal/dilewati</p>
            {result.failed.length > 0 && <ul>{result.failed.map((x, i) => <li key={i} data-testid={`import-failed-row-${i}`}>Baris {x.row}{x.nis ? ` (NIS ${x.nis})` : ""}: {x.reason}</li>)}</ul>}
          </div>
        )}
      </div>
    </Modal>
  );
};

const CardsModal = ({ close }) => {
  const [classes, setClasses] = useState([]);
  const [cls, setCls] = useState("");
  useEffect(() => { api("/students").then((r) => setClasses([...new Set(r.map((s) => s.class_name))].sort())).catch(() => {}); }, []);
  return (
    <Modal title="Cetak kartu siswa" close={close} testid="cards-modal">
      <div className="modal-form"><p className="muted">PDF A4 berisi 10 kartu ukuran KTP per halaman, lengkap dengan QR code untuk scan kehadiran.</p>
        <label>Kelas<select value={cls} onChange={(e) => setCls(e.target.value)} data-testid="cards-class-select"><option value="">Semua siswa aktif</option>{classes.map((c) => <option key={c}>{c}</option>)}</select></label>
        <button className="primary-btn full" onClick={() => download(`/students/cards/pdf?class_name=${encodeURIComponent(cls)}`)} data-testid="cards-download-button"><CreditCard size={16} /> Unduh kartu PDF</button></div>
    </Modal>
  );
};

const ConfirmDelete = ({ s, close, done }) => {
  const remove = async () => {
    try { await api(`/students/${s.id}/permanent`, { method: "DELETE" }); toast.success("Siswa dihapus, catatan tetap disimpan"); done(); close(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title="Hapus siswa?" close={close} testid="delete-student-modal">
      <div className="modal-form"><p className="muted">Data <b>{s.name}</b> (NIS {s.nis}) akan dihapus permanen. Catatan disiplin & prestasinya tetap tersimpan di laporan.</p>
        <div className="row-actions"><button className="outline-btn" onClick={close} data-testid="delete-cancel-button">Batal</button><button className="primary-btn danger-btn" onClick={remove} data-testid="delete-confirm-button"><Trash2 size={16} /> Hapus permanen</button></div></div>
    </Modal>
  );
};

export const StudentsPage = ({ user, openProfile }) => {
  const admin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("Aktif");
  const [modal, setModal] = useState(null);
  const load = () => api(`/students?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`).then(setRows).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (e, path, method, msg) => {
    e.stopPropagation();
    try { await api(path, { method }); toast.success(msg); load(); } catch (err) { toast.error(err.message); }
  };
  return (
    <div data-testid="students-page">
      <div className="page-heading compact">
        <div><p className="eyebrow">DATA MASTER</p><h1>Data siswa</h1><p className="muted">Kelola identitas dan status siswa di sekolah.</p></div>
        {admin && <div className="row-actions"><button className="outline-btn" onClick={() => setModal({ type: "cards" })} data-testid="print-cards-button"><CreditCard size={17} /> Cetak kartu</button><button className="outline-btn" onClick={() => setModal({ type: "import" })} data-testid="import-students-button"><Upload size={17} /> Unggah massal</button><button className="primary-btn" onClick={() => setModal({ type: "add" })} data-testid="add-student-button"><Plus size={17} /> Tambah siswa</button></div>}
      </div>
      <div className="toolbar">
        <div className="search"><Search size={17} /><input data-testid="student-search-input" placeholder="Cari nama atau NIS…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="filter-pills">{[["Aktif", "Aktif"], ["Tidak Aktif", "Arsip"]].map(([v, l]) => <button key={v} className={status === v ? "filter active" : "filter"} onClick={() => setStatus(v)} data-testid={`student-filter-${l.toLowerCase()}`}>{l}</button>)}</div>
        <span className="result-count" data-testid="student-count">{rows.length} siswa {status === "Aktif" ? "aktif" : "diarsipkan"}</span>
      </div>
      <section className="panel table-panel">
        <table><thead><tr><th>Siswa</th><th>NIS / NISN</th><th>Kelas</th><th>Program keahlian</th><th>Status</th><th>{admin ? "Aksi" : ""}</th></tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} data-testid={`student-row-${s.id}`} className="clickable-row" onClick={() => openProfile(s.id)}>
              <td><div className="student-cell"><div className="student-avatar">{s.name.slice(0, 1)}</div><b>{s.name}</b></div></td>
              <td>{s.nis}<small className="table-sub">{s.nisn || "NISN belum diisi"}</small></td>
              <td>{s.class_name}</td><td>{s.major}</td>
              <td><span className={`status ${s.status === "Aktif" ? "verified" : "waiting"}`}>{s.status === "Aktif" ? "Aktif" : "Diarsipkan"}</span></td>
              <td>{admin ? <div className="row-actions">
                {s.status === "Aktif"
                  ? <button className="mini-btn" title="Arsipkan" onClick={(e) => act(e, `/students/${s.id}`, "DELETE", "Siswa diarsipkan")} data-testid={`archive-student-${s.id}`}><Archive size={14} /> Arsipkan</button>
                  : <button className="mini-btn ok" onClick={(e) => act(e, `/students/${s.id}/restore`, "PATCH", "Siswa diaktifkan kembali")} data-testid={`restore-student-${s.id}`}><RotateCcw size={14} /> Aktifkan</button>}
                <button className="mini-btn danger" onClick={(e) => { e.stopPropagation(); setModal({ type: "delete", s }); }} data-testid={`delete-student-${s.id}`}><Trash2 size={14} /> Hapus</button>
              </div> : <ChevronRight size={16} className="muted" />}</td>
            </tr>))}
          </tbody></table>
        {!rows.length && <div className="empty">Tidak ada data siswa.</div>}
      </section>
      {modal?.type === "add" && <AddStudent close={() => setModal(null)} saved={load} />}
      {modal?.type === "import" && <ImportStudents close={() => setModal(null)} saved={load} />}
      {modal?.type === "cards" && <CardsModal close={() => setModal(null)} />}
      {modal?.type === "delete" && <ConfirmDelete s={modal.s} close={() => setModal(null)} done={load} />}
    </div>
  );
};
